import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import _snakeCase from 'lodash/snakeCase';
import { CSS_VARS } from '@/constants/css-vars';
import { ICONS } from '@/constants/icons';

import type { BusinessDocumentType } from '@/types/business';

import { useParseFileName } from '@/utils/hooks/useParseFileName';
import CheckCameraCapture from '@/components/Camera/CheckCameraCapture';

interface UploadDocumentsProps<DT extends BusinessDocumentType> {
  documentType: DT;
  label: string;
  disabled?: boolean;
  documentId?: string;
  errorMessage?: string | null;
  fileName?: string;
  subtitle?: string[];
  onFileUpdate?: (
    documentType: DT,
    file: React.ChangeEvent<HTMLInputElement>,
    documentId: string,
  ) => void;
  onFileUpload?: (
    documentType: DT,
    file: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  required?: boolean;
  accept?: string;
  enableCamera?: boolean;
  isUploading?: boolean;
  aspectRatio?: number;
  uploadButtonSx?: React.CSSProperties;
}

const ACCEPT_FILES = 'image/png,image/jpeg,image/jpg,image/*,.pdf,.zip';

const UploadDocuments = <
  DocumentType extends BusinessDocumentType = BusinessDocumentType,
>(
  props: UploadDocumentsProps<DocumentType>,
): JSX.Element => {
  const {
    documentType,
    label,
    disabled = false,
    documentId,
    fileName,
    subtitle,
    onFileUpdate,
    onFileUpload,
    errorMessage,
    required = false,
    accept = ACCEPT_FILES,
    enableCamera = false,
    isUploading = false,
    uploadButtonSx,
  } = props;

  const [showCamera, setShowCamera] = useState(false);
  const isUploaded = Boolean(documentId && fileName);

  const parsedFileName = useParseFileName(fileName ?? '');

  const hasDocument = !!fileName && !!documentId;

  const Icon = hasDocument ? ICONS.BiCheckCircle : ICONS.BiCircle;

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onFileUpload?.(documentType, event);
  };

  const handleUpdate = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (documentId == null) return;
    onFileUpdate?.(documentType, event, documentId);
  };

  const handleCameraCapture = async (imageSrc: string): Promise<void> => {
    try {
      const res = await fetch(imageSrc);
      const blob = await res.blob();

      // Use PNG format for maximum quality (lossless)
      const file = new File([blob], `${_snakeCase(label)}-${Date.now()}.png`, {
        type: 'image/png',
      });

      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);

      const mockTarget = {
        files: dataTransfer.files,
      } as unknown as HTMLInputElement;
      const syntheticEvent = {
        target: mockTarget,
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      if (hasDocument) {
        handleUpdate(syntheticEvent);
      } else {
        handleUpload(syntheticEvent);
      }

      setShowCamera(false);
    } catch (error) {
      console.error('Error converting image:', error);
      setShowCamera(false);
    }
  };

  const handleOpenCamera = (): void => {
    setShowCamera(true);
  };

  const handleCloseCamera = (): void => {
    setShowCamera(false);
  };

  const isMobile =
    /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      typeof navigator !== 'undefined' ? navigator.userAgent : '',
    );

  return (
    <Box
      border="0.1rem solid #e4e4e4"
      borderRadius="0.8rem"
      marginTop="2rem"
      width="100%"
      sx={{
        display: 'flex',
        padding: { xs: '1.6rem', md: '2rem' },
      }}
    >
      <Grid sx={{ flex: 1 }}>
        <Grid
          alignItems="flex-start"
          container
          gap="1rem"
          justifyContent="space-between"
          wrap="nowrap"
        >
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                fontSize: { xs: '2.5rem', md: '2.7rem' },
                marginRight: '1rem',
              }}
            >
              <Icon
                color={
                  hasDocument
                    ? `var(${CSS_VARS.Colors.Success.Dark})`
                    : `var(${CSS_VARS.Colors.Gray[300]})`
                }
              />
            </Box>
            <Box>
              <Typography
                fontWeight={900}
                variant="h5"
                sx={{
                  wordBreak: 'break-word',
                  maxWidth: { xs: '100%', sm: '24rem', md: '32rem' },
                }}
              >
                {label}
                {required && (
                  <span style={{ color: `var(${CSS_VARS.Colors.Error.Dark})` }}>
                    *
                  </span>
                )}
              </Typography>

              {subtitle?.map((subtitle) => (
                <Typography
                  color={`var(${CSS_VARS.Colors.Gray[400]})`}
                  key={subtitle}
                  variant="subtitle2"
                >
                  {subtitle}
                </Typography>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 2,
            }}
          >
            {isMobile && enableCamera && (
              <Button
                onClick={handleOpenCamera}
                disabled={disabled || isUploading}
                size="large"
                variant={!isUploaded ? 'contained' : 'outlined'}
                sx={{
                  width: 160,
                  backgroundColor: !isUploaded ? '#254D73' : 'white',
                  color: '#9a9a9d',
                  fontWeight: 700,
                  fontSize: '1.8rem',
                  marginLeft: { xs: 'auto', md: '1rem' },
                  minWidth: 81,
                }}
              >
                <Typography
                  color={`var(${
                    disabled || isUploading
                      ? CSS_VARS.Colors.Gray[300]
                      : !isUploaded
                        ? CSS_VARS.Colors.White
                        : CSS_VARS.Colors.BlackBlue
                  })`}
                  fontWeight={700}
                  variant="body1"
                >
                  {!isUploaded ? 'Take Photo' : 'Retake Photo'}
                </Typography>
              </Button>
            )}

            <Button
              component="label"
              disabled={disabled || isUploading}
              size="large"
              variant={isUploaded ? 'outlined' : 'contained'}
              sx={{
                width: 81,
                ...uploadButtonSx,
                backgroundColor: isUploaded ? 'white' : '#254D73',
                color: '#9a9a9d',
                fontSize: '1.8rem',
                fontWeight: 700,
                marginLeft: { xs: 'auto', md: '1rem' },
                minWidth: 80,
              }}
            >
              <Typography
                color={`var(${
                  disabled || isUploading
                    ? CSS_VARS.Colors.Gray[300]
                    : !isUploaded
                      ? CSS_VARS.Colors.White
                      : CSS_VARS.Colors.BlackBlue
                })`}
                fontWeight={700}
                variant="body1"
              >
                {isUploaded ? 'Update' : 'Upload'}
              </Typography>
              <input
                accept={accept}
                hidden
                multiple
                type="file"
                onChange={isUploaded ? handleUpdate : handleUpload}
              />
            </Button>
          </Box>
        </Grid>
        <Box marginTop={2}>
          {fileName != null && (
            <Grid container width={{ xs: '100%', md: 'fit-content' }}>
              <Typography
                color={`var(${CSS_VARS.Colors.Gray[400]})`}
                fontWeight={700}
                variant="subtitle1"
                sx={{ marginRight: '0.5rem' }}
              >
                Chosen file:
              </Typography>
              <Typography
                color={`var(${CSS_VARS.Colors.BrandBlue[400]})`}
                variant="subtitle1"
                sx={{ wordBreak: 'break-all' }}
              >
                {` ${parsedFileName.name}${parsedFileName.extension}`}
              </Typography>
            </Grid>
          )}
          {errorMessage != null && (
            <Grid alignItems="center" container marginLeft="3rem">
              <Typography
                color={`var(${CSS_VARS.Colors.Error.Dark})`}
                fontWeight={700}
                variant="h6"
              >
                {errorMessage}
              </Typography>
            </Grid>
          )}
        </Box>
      </Grid>

      {/* Camera Modal */}
      {showCamera && (
        <CheckCameraCapture
          onCapture={handleCameraCapture}
          onClose={handleCloseCamera}
          title={`Capture ${label}`}
          aspectRatio={2}
        />
      )}
    </Box>
  );
};
export default UploadDocuments;
